import { trackWsEvent } from './debug/wsDebug'
import { connectSocket, disconnectSocket, isPrimeAwareConfig } from './internal/socketConnection'
import {
  setSocketConfigs,
  eventHandlers,
  activeConnections,
  consumerCache,
  connectionCleanups,
  socketConfigs,
  type Handler,
} from './internal/socketInternalState'
import { cleanPath, type FullSocketConfig } from './lib/socketMatcher'

export { handleBrowserRouteChange, connectSocket, disconnectSocket } from './internal/socketConnection'
export type { FullSocketConfig } from './lib/socketMatcher'
export type { WSPayload, Handler } from './internal/socketInternalState'

/**
 * Register socket configurations.
 */
export const registerConfigs = (configs: FullSocketConfig[]) => {
  setSocketConfigs(configs)
}

/**
 * Registers an event handler for a specific socket and event.
 */
export const socketOn = (name: string, event: string, handler: Handler) => {
  let socketMap = eventHandlers.get(name)
  if (!socketMap) {
    socketMap = new Map()
    eventHandlers.set(name, socketMap)
  }

  let handlerSet = socketMap.get(event)
  if (!handlerSet) {
    handlerSet = new Set()
    socketMap.set(event, handlerSet)
  }

  // Count only genuinely new registrations so debug stats reflect real churn.
  if (!handlerSet.has(handler)) {
    handlerSet.add(handler)
    trackWsEvent('subscribe', name, event)
  }

  return () => socketOff(name, event, handler)
}

/**
 * Removes an event handler.
 */
const socketOff = (name: string, event: string, handler: Handler) => {
  const socketMap = eventHandlers.get(name)
  const handlerSet = socketMap?.get(event)

  // Count only actual removals so a double-unsubscribe cannot inflate debug stats.
  if (handlerSet?.delete(handler)) {
    trackWsEvent('unsubscribe', name, event)
    if (handlerSet.size === 0) {
      socketMap?.delete(event)
    }
  }
}

/**
 * Emits an event to the server over a specific socket connection.
 */
export const socketEmit = (name: string, event: string, payload: Record<string, unknown> = {}) => {
  trackWsEvent('emit', name, event)
  const entry = activeConnections.get(name)
  if (entry) {
    try {
      entry.subscription.perform(event, payload)
    } catch (e) {
      console.error(`socketEmit error for ${name}`, e)
    }
  }
}

/**
 * Reconnects a specific socket by name (disconnect + connect).
 * Useful when subscription params change (e.g. session user becomes available).
 */
export const reconnectSocket = (name: string) => {
  const cfg = socketConfigs.find((c) => c.name === name)
  if (!cfg) return

  const entry = activeConnections.get(name)
  const path = cleanPath(entry?.path ?? window.location.pathname)
  if (!path) return

  const handlers = eventHandlers.get(name)
  disconnectSocket(name)
  if (handlers) eventHandlers.set(name, handlers)
  connectSocket(cfg, path)
}

/**
 * Reconnects only sockets whose channel accepts the `prime` flag. Other
 * channels are left untouched, since their subscription identifier does not
 * depend on prime mode.
 */
export const reconnectPrimeAwareSockets = () => {
  Array.from(activeConnections.entries()).forEach(([name, entry]) => {
    const cfg = socketConfigs.find((c) => c.name === name)
    if (cfg && isPrimeAwareConfig(cfg, entry.path)) reconnectSocket(name)
  })
}

/**
 * Cleans up all active connections and internal state.
 */
export const cleanupAll = () => {
  Array.from(activeConnections.keys()).forEach(disconnectSocket)

  eventHandlers.clear()
  connectionCleanups.clear()
  consumerCache.clear()
  setSocketConfigs([])
}

/**
 * Logs socket-related messages in non-production environments.
 */
export const socketLog = (message: string, ...rest: string[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[Socket] ${message}`, ...rest)
  }
}
